import Button from '@/components/Button'
import Layout, { Stack } from '@/components/Layout'
import Text from '@/components/Text'
import { getConfig } from '@/lib/config'
import { createUserPartnerAssociation, PARTNER_METADATA_COOKIE } from '@/lib/partners'
import { QUERY_PARTNER_ASSOCIATIONS } from '@/service/query'
import type { CurrentUser } from '@/shared/api-generated-types'
import Modal from '@/widgets/Modal'
import { deleteCookie } from 'cookies-next'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { useClient } from 'urql'

const { publicRuntimeConfig } = getConfig()

interface Props {
  isOpen: boolean
}

const PartnerSwitchDialog = (props: Props): JSX.Element => {
  const router = useRouter()
  const [loading, setLoading] = useState(false)
  const [stayLoading, setStayLoading] = useState(false)
  const client = useClient()

  function deletePartnerCookies(): void {
    const domain = publicRuntimeConfig.auth.partnerCookieDomain
    deleteCookie('auth.partner', { path: '/', domain })
    deleteCookie(PARTNER_METADATA_COOKIE, { path: '/', domain })
  }

  async function handleStayWithCurrentPartner(): Promise<void> {
    setStayLoading(true)
    deletePartnerCookies()

    const { data } = await client.query<{ currentUser: CurrentUser }>(QUERY_PARTNER_ASSOCIATIONS, {}).toPromise()
    const primaryAssociation = data?.currentUser?.authenticatedUser?.partnerAssociations?.find(
      (association) => association?.isPrimary && association?.active,
    )

    if (primaryAssociation?.partner?.slug) {
      window.location.href = `/partner/${primaryAssociation.partner.slug}`
    } else {
      router.push('/')
    }
  }

  async function handleSwitchPartner(): Promise<void> {
    const partnerSlug = router.query.slug as string | undefined
    if (!partnerSlug) return
    setLoading(true)
    await createUserPartnerAssociation(partnerSlug)
    setTimeout(() => window.location.reload(), 1000)
  }

  return (
    <Modal isOpen={props.isOpen} isClosable={false}>
      <Layout className="p-4" spacing="lg">
        <Stack y>
          <div className="flex gap-2 items-center">
            <img src="/images/alert.svg" loading="lazy" width="28px" />
            <Text size="md" weight="lg">
              You're on a Different Learning Channel
            </Text>
          </div>
          <Text size="xs">It looks like you are already part of a different GetSetUp Partner Learning Channel.</Text>
          <Text size="xs" weight="lg" className="my-2!">
            🧡 Thinking of Switching?
          </Text>
          <Text size="xs">
            Please note:
            <br />
            <ul className="list-disc">
              <li>
                Your new GetSetUp experience may be limited in this new channel as different partners offer different
                class catalogs.
              </li>
              <li>This means you may lose access to some classes, articles and Helen, your Digital Assistant</li>
            </ul>
          </Text>
          <Text size="xs">
            If you prefer not to continue, click the <span className="font-semibold">Back</span> button to return
          </Text>
          <Text size="xs">
            If you're note sure, give us a call at <a href="tel:1-888-559-1614">1-888-559-1614</a> - we're happy to
            help!
          </Text>
          <Stack x spaceBetweenX="sm" justifyContent="center">
            <Button
              label="Back"
              theme="primary"
              onClick={handleStayWithCurrentPartner}
              disabled={stayLoading || loading}
              loading={stayLoading}
            />
            <Button
              disabled={loading || stayLoading}
              loading={loading}
              onClick={handleSwitchPartner}
              label="Switch Anyway"
              theme="secondary"
            />
          </Stack>
        </Stack>
      </Layout>
    </Modal>
  )
}

export default PartnerSwitchDialog
